import ErrorBox from "../components/Error/Box";
import TeamsSidebar from "@/components/Sidebars/Teams";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import Chats from "@/components/Sidebars/Chats";
import TextChannel from "./Channel/Text/Messages";
import Bottom from "./Bottom/Bottom";
import Hammer from "hammerjs";
import StreamerModeAlert from "@/components/Error/Alerts/StreamerMode";
import VerifyEmailAlert from "@/components/Error/Alerts/VerifyEmail";
import OpenNotifications from "@/components/Error/Alerts/OpenNotifications";
import { useMediaQuery } from "@mui/material";
export default function ChatDisplay({  socket }: { socket: any }) {
    const { channelId } = useParams();
    const [chats, setChats] = useState<any[]>([]);
    const queryClient = useQueryClient();
    const query = queryClient.getQueryState(["user"]) as any;
  
    const setHidden = (channelId: string) => {
        fetch(`__SITE_BASE__/api/v1/me/chats/${channelId}/hidden`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            }
        });

        setChats((prevChats) => prevChats.filter((c) => c.id !== channelId));

        if (channelId == useParams().channelId) {
            window.location.href = "/chat";
        }
    }

    const chatsQuery = useQuery({
        queryKey: ["chats"],
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        enabled: !!query.data?.user,
        queryFn: async () => {
            const response = await fetch(`__SITE_BASE__/api/v1/me/chats`);
            const json = await response.json();
            setChats(json.dms.sort((a: any, b: any) => new Date(b.lastMessage?.createdAt).getTime() - new Date(a.lastMessage?.createdAt).getTime()));
            return json;
        }
    });

    const handleSocketMessage = useCallback((event: MessageEvent) => {
        const data = JSON.parse(event.data);
        if (data.t === "MESSAGE_SEND" && data.d.channelType === "dm") {
            setChats((prevChats) => {
                let channel = prevChats.find((c) => c.id === data.d.channelId);
                if (channel) {
                    channel.lastMessage = data.d.message;
                    return [channel, ...prevChats.filter((c) => c.id !== data.d.channelId)];
                }
                return prevChats;
            });
        } else if (data.t === "PRESENCE_UPDATE") {
            setChats((prevChats) => {
                const channel = prevChats.find((c) => c.users[0].id === data.d.userId);
                if (channel) {
                    channel.users[0].presence = data.d.presence;
                    return [channel, ...prevChats.filter((c) => c.users[0].id !== data.d.userId)];
                }
                return prevChats;
            });
        } else if (data.t == "USER_PROFILE_UPDATED") {
            setChats((prevChats) => {
                const channel = prevChats.find((c) => c.users[0].id === data.d.user.id);
                if (channel) {
                    channel.users[0] = data.d.user;
                    return [channel, ...prevChats.filter((c) => c.users[0].id !== data.d.user.id)];
                }
                return prevChats;
            });
        } else if (data.t === "MESSAGE_DELETED") {
            setChats((prevChats) => {
                const channel = prevChats.find((c) => c.id === data.d.channelId);
                if (channel) {
                    channel.lastMessage = {isDeleted: true};
                    return [channel, ...prevChats.filter((c) => c.id !== data.d.channelId)];
                }
                return prevChats;
            });
        } else if (data.t === "MESSAGE_UPDATED") {
            setChats((prevChats) => {
                const channel = prevChats.find((c) => c.id === data.d.channelId);
                if (channel) {
                    channel.lastMessage = data.d.message;
                    return [channel, ...prevChats.filter((c) => c.id !== data.d.channelId)];
                }
                return prevChats;
            });
        }
    }, []);

    useEffect(() => {
        if (!socket) return;

        socket.addEventListener("message", handleSocketMessage);
        return () => {
            socket.removeEventListener("message", handleSocketMessage);
        };
    }, [socket, handleSocketMessage]);

    const ref = useRef<HTMLDivElement>(null);
    const [count, setCount] = useState(1);

    useEffect(() => {
        if (channelId) setCount(1);
        if (channelId && socket) socket.send(JSON.stringify({ t: "CHANNEL_MOUNTED", d: { channelId } }));
    }, [channelId]);

    useEffect(() => {
        if (window.location.pathname === "/chat") setCount(2);
    }, []);


    useEffect(() => {
        if (!ref.current) return;
        const hammer = new Hammer(ref.current);
        hammer.on("swiperight", () => {
            if (window.location.pathname === "/chat") return;
            if (count < 2) setCount(prevCount => prevCount + 1);
        });
        hammer.on("swipeleft", () => {
            if (window.location.pathname === "/chat") return;
            if (count > 0) setCount(prevCount => prevCount - 1);
        });
        return () => hammer.destroy();
    }, [ref.current, count]);

    const [streamerMode, setStreamerMode] = useState(() => {
        try {
            const storedMode = localStorage.getItem("streamerMode");
            if (!storedMode) {
                localStorage.setItem("streamerMode", JSON.stringify({
                    enabled: false,
                    shortcut: null,
                    details: {
                        hidePersonalInfo: true,
                        hideInviteLinks: true,
                        hideLastMessagesOnDMs: true,
                    }
                }));
            }
            return storedMode ? JSON.parse(storedMode) : {
                enabled: false,
                shortcut: null,
                details: {
                    hidePersonalInfo: true,
                    hideInviteLinks: true,
                    hideLastMessagesOnDMs: true,
                }
            };
        } catch (e) {

            console.error(e);
            return {}
        }
    });

    useEffect(() => {
        const handleStorageChange = () => {
            const newValue = JSON.parse(localStorage.getItem("streamerMode") || "{}");
            setStreamerMode(newValue);
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);
    const isMobile = useMediaQuery('(max-width:600px)');
    return (
        <>
            {!socket && (
                <div className="fixed top-0 items-center justify-center h-screen w-full z-50">
                    <div className="flex items-center justify-center w-full bg-yellow-400">
                        <p className="text-white font-semibold text-lg">
                            The connection to the server has been lost. Attempting to reconnect...
                        </p>
                    </div>
                </div>
            )}

            <div className={`flex flex-col h-screen`} ref={ref}>
                <OpenNotifications />
                <StreamerModeAlert socket={socket} streamerMode={streamerMode} />
                <VerifyEmailAlert socket={socket} streamerMode={streamerMode} user={query.data?.user} />
                <div className="flex flex-1 overflow-hidden">
                    {isMobile && (
                    <TeamsSidebar
                        socket={socket}
                        isOpen={count}
                    />
                    )}
                    <div className="flex flex-1 overflow-hidden">
                        {chatsQuery.isLoading || !query.data?.user || socket?.readyState !== WebSocket.OPEN ? (
                            <Chats socket={socket} chats={null} isOpen={count} setHidden={setHidden} streamerMode={streamerMode} />
                        ) : chatsQuery.isError ? (
                            <ErrorBox message={(chatsQuery.error as Error)?.message || 'An error occurred'} />
                        ) : (
                            <Chats socket={socket} chats={chats} isOpen={count} setHidden={setHidden} streamerMode={streamerMode} />
                        )}

                        {channelId && chats && (
                            <div className="sm:p-4 w-full sm:h-full flex">
                                <TextChannel
                                    key={channelId}
                                    channel={{ id: channelId, name: `${chats.find((c) => c.id === channelId)?.users[0].username}`, type: "DM", users:chats.find((c) => c.id === channelId)?.users || []} as any}
                                    isDM={true}
                                />
                            </div>
                        )}

                    </div>
                </div>
                {!isMobile && <TeamsSidebar socket={socket} isOpen={count} />}
                <Bottom setIsOpen={setCount} />
            </div>
        </>
    );
}